import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import type { Sistema } from '../api';
import { listarSistemas } from './api';

/**
 * El estado de servidor del ABM de sistemas conectados.
 *
 * **No hay caché optimista**, por lo mismo que en el ABM de usuarios: después de
 * cada escritura se re-lee la lista entera. Acá pesa todavía más — esta pantalla
 * dice **qué app puede facturar a nombre de qué contribuyente**, y mostrar una
 * lista blanca supuesta por el cliente sería mostrar un permiso que quizá no
 * existe.
 *
 * ⚠️ **La API key en claro NUNCA entra a este caché.** Las respuestas de alta y
 * de rotación no son queries: son mutaciones, su dato vive lo que dura el panel
 * que lo muestra y se descarta con `reset()`. Lo que se cachea es la lista, y la
 * lista no tiene dónde traer una key.
 */

/** Clave de la lista. Una constante para que invalidar y consultar no se separen. */
export const CLAVE_SISTEMAS = ['sistemas'] as const;

/** La lista completa: activos e inactivos, cada uno con su lista blanca. */
export function useSistemas(): UseQueryResult<Sistema[], Error> {
  return useQuery({ queryKey: CLAVE_SISTEMAS, queryFn: listarSistemas });
}

/** Vuelve a pedir la lista después de una escritura. */
export function useRefrescarSistemas(): () => void {
  const cliente = useQueryClient();
  return useCallback(() => {
    void cliente.invalidateQueries({ queryKey: CLAVE_SISTEMAS });
  }, [cliente]);
}
